
import React, { useEffect, useState } from "react"
import { Form } from "react-bootstrap"
import { injectIntl } from "react-intl"
import { isError, useQuery, useQueryClient } from "react-query"
import PaymentMethod from "../../payment-account/model/paymentmethod"
import Session from "../../payment-account/model/session"
import PaymentMethodService from "../../payment-account/service/paymentMethodService"
import { usePaymentAccountSession } from "../../payment-processing/usePaymentAccountSession"
import { HttpError } from "../../state/helpers/Error"
import IntlProps from "../../state/helpers/IntlProps"
import { CardOnFile } from "../../state/listing/contact/models"
import { SESSION_TYPE } from "../../utils/payment-account/constants"
import CircleNumberBadge from "../badge/circle-badge/CircleNumberBadge"
import DebouncedInput from "../inputs/DebouncedInput"
import { RF_COLOR_CODES, RF_METHOD_TYPES } from "./rf-components/constants"
import { messages } from "./rf-components/messages"
import RainforestDisabled from "./rf-components/RainforestDisabled"
import RainforestLoading from "./rf-components/RainforestLoading"
import RainforestPayment from "./rf-components/RainforestPayment"
import "./rf-components/styles/rainforest-credit-card.scss"

type OwnProps = {
    contactId: string
    refreshContact: () => void
    onCardAdded?: (newCardOnFile: CardOnFile) => void
    locationId : string
}

const NewRainforestCreditCardWrapper = (props: OwnProps & IntlProps) => {

    const [cardholderName, setCardholderName] = useState<string>("")
    const [isTyping, setIsTyping] = useState<boolean>(false)
    const [isRFLoaded, setisRFLoaded] = useState(false)

    const queryClient = useQueryClient()

    const { session, merchantID, isLoading: isLoadingSession } = usePaymentAccountSession(SESSION_TYPE.CREATE_PAYMENT_METHOD)

    const { data: paymentMethodData, isLoading: isLoadingData, isFetching } = useQuery(["getPaymentMethodConfig", cardholderName], () => {
        return PaymentMethodService.createPaymentMethodConfig(props.contactId, merchantID, cardholderName)
    }, {
        enabled: !isLoadingSession && cardholderName !== "" && !isTyping && !!merchantID,
    })

    useEffect(() => {
        const component = document.querySelector("rainforest-payment")
        if (component) {
            const delay = setTimeout(() => {
                setisRFLoaded(true)
                clearTimeout(delay)
            }, 1000)
        }
    })

    const handleApproved = async (event) => {
        const paymentMethodId = event.detail[0].data.payment_method_id
        const fingerprintID = event.detail[0].data.fingerprint
        PaymentMethodService.addCardOnFile(paymentMethodId, fingerprintID, props.contactId, props.locationId).then((cardOnFile) => {
            setTimeout(() => {
                if (props.refreshContact) {
                    props.refreshContact()
                } else if (props.onCardAdded && cardOnFile) {
                    props.onCardAdded(cardOnFile)
                }
                queryClient.invalidateQueries("getPaymentMethodConfig", { exact: true })
            }, 500)
        })
    }

    const isPaymentMethod = (paymentMethod: PaymentMethod | HttpError | undefined)
        : paymentMethod is PaymentMethod => {
        return (
            paymentMethod != undefined && !isError(paymentMethod)
        )
    }

    const isLoading = isLoadingData || isLoadingSession || isFetching || isTyping
    const displayLoading = (!isRFLoaded && cardholderName !== "") || isLoading
    const displayDisabled = !isLoading && cardholderName === ""
    const displayRainforest = !isLoading && isPaymentMethod(paymentMethodData)
    return (
        <>
            <div className="container-rf">
                <div className="flex-wrap d-flex header-row">
                    <CircleNumberBadge bgColor={RF_COLOR_CODES.BLUE} fontColor={RF_COLOR_CODES.WHITE} number="1" />
                    <Form.Label className="ml-2 mt-1 mb-3">
                        {props.intl.formatMessage(messages.cardholderName)}
                    </Form.Label>
                </div>
                <DebouncedInput
                    onInputChange={(name: string) => setCardholderName(name)}
                    onTypingStatusChange={(isTyping: boolean) => setIsTyping(isTyping)}
                    placeholder={props.intl.formatMessage(messages.cardholderName)}
                    className="cardholder-input"
                />
                <div className="flex-wrap d-flex header-row mt-3">
                    <CircleNumberBadge
                        bgColor={isRFLoaded ? RF_COLOR_CODES.BLUE : RF_COLOR_CODES.GREY} fontColor={RF_COLOR_CODES.WHITE} number="2" />
                    <Form.Label className="ml-2 mt-1">
                        {props.intl.formatMessage(messages.cardInformation)}
                    </Form.Label>
                </div>
                <div className={displayDisabled ? "fade-in" : "fade-out"}>
                    <RainforestDisabled method={RF_METHOD_TYPES.SAVE_CARD} />
                </div>
                <div className={displayLoading ? "fade-in" : "fade-out"}>
                    <RainforestLoading method={RF_METHOD_TYPES.SAVE_CARD} />
                </div>
            </div>
            {displayRainforest &&
                <div className={!isRFLoaded ? "fade-out" : "fade-in"}>
                    <RainforestPayment
                        sessionKey={(session as Session).getSessionKey()}
                        paymentMethodConfigID={paymentMethodData.getId()}
                        allowedMethods='CARD'
                        onSubmit={handleApproved}
                        method={RF_METHOD_TYPES.SAVE_CARD}
                    />
                </div>
            }
        </>
    )
}
export default injectIntl(NewRainforestCreditCardWrapper)



// WEBPACK FOOTER //
// ./src/common/credit-card/NewRainforestCreditCardWrapper.tsx